import { useEffect, useRef, useState, memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Copy, Trash2, Plus, Move } from 'lucide-react';
import { useStore } from './store';
import { useChatStore } from './chatStore';
import { TYPE_CONFIG } from './constants';
import { createPortal } from 'react-dom';

let movingNodeId = null;

const CustomNodeComponentInner = ({ id, data, selected }) => {
   const nodeData = data || {};
   const config = TYPE_CONFIG[nodeData.type] || TYPE_CONFIG.message;
   const Icon = config.icon;

   const duplicateNode = useStore((s) => s.duplicateNode);
   const deleteNode = useStore((s) => s.deleteNode);
   const addChildNode = useStore((s) => s.addChildNode);
   const setSelectedNodeId = useStore((s) => s.setSelectedNodeId);
   const attachSubtree = useStore((s) => s.attachSubtree);
   const selectedNodeId = useStore((s) => s.selectedNodeId);
   const isExecuting = useChatStore((s) => s.currentNodeId === id);

  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const menuRef = useRef(null);
  const menuPortalRef = useRef(null);
  const contextMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail !== id) setIsMoving(false);
    };
    window.addEventListener('node-move-mode', handler);
    return () => window.removeEventListener('node-move-mode', handler);
  }, [id]);

  const openMenu = () => {
    setShowAddMenu(true);
    const nodes = useStore.getState().nodes;
    useStore.getState().setNodes(nodes.map(n => ({ ...n, zIndex: n.id === id ? 1000 : 0 })));
  };
  const closeMenu = () => {
    setShowAddMenu(false);
    useStore.getState().setNodes(useStore.getState().nodes.map(n => ({ ...n, zIndex: 0 })));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          menuPortalRef.current && !menuPortalRef.current.contains(event.target)) closeMenu();
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) closeContextMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') { closeMenu(); closeContextMenu(); setIsMoving(false); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAddMenu, showContextMenu]);

  const handleRightClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowContextMenu(true);
    useStore.getState().setNodes(
      useStore.getState().nodes.map(n => ({ ...n, zIndex: n.id === id ? 1000 : 0 }))
    );
  };

  const closeContextMenu = () => {
    setShowContextMenu(false);
    useStore.getState().setNodes(useStore.getState().nodes.map(n => ({ ...n, zIndex: 0 })));
  };

  const handleStartMove = () => {
    closeContextMenu();
    setIsMoving(true);
    movingNodeId = id;
    window.dispatchEvent(new CustomEvent('node-move-mode', { detail: id }));
  };

  const isWebhookResult = nodeData.type === 'webhook_result';
  const isSystemNode = false;
  const resultColor = nodeData.webhookBranch === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500';
  const resultBorder = nodeData.webhookBranch === 'success' ? 'border-emerald-200' : 'border-rose-200';

  const handleNodeClick = (e) => {
    if (movingNodeId && movingNodeId !== id) {
      e.stopPropagation();
      attachSubtree(movingNodeId, id);
      movingNodeId = null;
      window.dispatchEvent(new CustomEvent('node-move-mode', { detail: null }));
      return;
    }
    setSelectedNodeId(id);
  };

  const isSelected = selected || selectedNodeId === id;

  return (
    <div
      onClick={handleNodeClick}
      onContextMenu={handleRightClick}
      className={`relative group px-3.5 py-2.5 rounded-xl border bg-white shadow-2xs hover:shadow-xs w-[210px] h-[68px] transition-all select-none ${
        isWebhookResult ? resultBorder : isSelected ? 'ring-2 ring-zinc-900 border-zinc-900' : 'hover:border-zinc-300 border-zinc-200'
      } ${isExecuting ? 'ring-2 ring-indigo-600 border-indigo-600' : ''} ${
        isMoving ? 'ring-2 ring-amber-500 border-amber-500' : ''
      } ${
        movingNodeId && movingNodeId !== id ? 'cursor-crosshair ring-2 ring-emerald-500 hover:ring-emerald-600' : ''
      }`}
    >
      {nodeData.type !== 'start' && (
        <Handle
          type="target"
          position={Position.Left}
          className="target-handle"
        />
      )}
      
      <div className="flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-lg ${isWebhookResult ? resultColor : config.color} flex items-center justify-center shrink-0 overflow-hidden`}>
          {typeof Icon === 'string' ? (
            <img src={Icon} alt="" className="w-full h-full object-contain scale-110" />
          ) : (
            <Icon size={18} className="opacity-95" />
          )}
        </div>
        <div className="flex-1 overflow-hidden min-w-0">
          <p className="text-[9px] font-semibold text-zinc-400 uppercase tracking-wider leading-none mb-1">
            {config.label} {nodeData.customId ? `#${nodeData.customId}` : ''}
          </p>
          <p className="text-xs font-semibold text-zinc-900 leading-tight truncate">
            {nodeData.label}
          </p>
        </div>
      </div>

      {nodeData.type === 'condition' && nodeData.branches?.map((b, i) => (
        <Handle
          key={b.id}
          type="source"
          position={Position.Right}
          id={b.id}
          style={{ top: `${(i + 1) * (100 / (nodeData.branches.length + 1))}%` }}
          className="source-handle !bg-zinc-300"
        />
      ))}

      {nodeData.type === 'action' && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="success"
            style={{ top: '30%' }}
            className="source-handle !bg-emerald-500"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="failure"
            style={{ top: '70%' }}
            className="source-handle !bg-rose-500"
          />
        </>
      )}

      {nodeData.type !== 'condition' && nodeData.type !== 'action' && (
        <Handle
          type="source"
          position={Position.Right}
          className="source-handle"
        />
      )}

      {!isSystemNode && (
        <div className="absolute -right-7 top-1/2 -translate-y-1/2 z-[9999]" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              showAddMenu ? closeMenu() : openMenu();
            }}
            className={`w-5 h-5 bg-zinc-900 text-white rounded-full flex items-center justify-center shadow-xs hover:scale-110 hover:bg-black transition-all border border-white ${
              showAddMenu ? 'opacity-100 scale-110' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            <Plus size={12} />
          </button>
        </div>
      )}

      {showAddMenu && menuRef.current && createPortal(
        <div 
          ref={menuPortalRef}
          className="fixed bg-white border border-zinc-200 rounded-xl shadow-xl p-1 min-w-[155px] grid grid-cols-1 gap-0.5 z-[9999]"
          style={{ 
            left: menuRef.current.getBoundingClientRect().right + 8,
            top: menuRef.current.getBoundingClientRect().top,
          }}
        >
          {Object.entries(TYPE_CONFIG).filter(([type]) => type !== 'start' && type !== 'webhook_result').map(([type, cfg]) => {
            const ItemIcon = cfg.icon;
            return (
              <button
                key={type}
                onClick={(e) => {
                  e.stopPropagation();
                  addChildNode(id, type);
                  closeMenu();
                }}
                className="flex items-center gap-2.5 px-2.5 py-1.5 hover:bg-zinc-100 rounded-lg transition-colors group/item text-left"
              >
                <div className={`w-6 h-6 rounded-md ${cfg.color} flex items-center justify-center`}>
                  <ItemIcon size={13} />
                </div>
                <span className="text-xs font-medium text-zinc-800">{cfg.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}

      {!isSystemNode && (
        <div className={`absolute -top-9 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-white border border-zinc-200 rounded-lg p-0.5 shadow-md transition-opacity z-[100] ${
          selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}>
          <button 
            onClick={(e) => { e.stopPropagation(); duplicateNode(id); }}
            className="p-1 hover:bg-zinc-100 rounded-md text-zinc-500 hover:text-zinc-900 transition-colors"
            title="Duplicate Node"
          >
            <Copy size={13} />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); deleteNode(id); }}
            className="p-1 hover:bg-rose-50 rounded-md text-zinc-500 hover:text-rose-600 transition-colors"
            title="Delete Node"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
      
      {isMoving && (
        <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-amber-500 text-white text-[9px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-md shadow-sm">
          Click target node to attach
        </div>
      )}

      {showContextMenu && (
        <div
          ref={contextMenuRef}
          className="absolute left-full top-1/2 -translate-y-1/2 ml-2 bg-white border border-zinc-200 rounded-xl shadow-xl p-1 min-w-[170px] z-[1000]"
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={(e) => { e.stopPropagation(); handleStartMove(); }}
            className="flex items-center gap-2.5 w-full px-2.5 py-1.5 hover:bg-zinc-100 rounded-lg transition-colors text-left"
          >
            <div className="w-6 h-6 rounded-md bg-zinc-100 flex items-center justify-center text-zinc-700">
              <Move size={13} />
            </div>
            <span className="text-xs font-medium text-zinc-800">Move with children</span>
          </button>
        </div>
      )}
    </div>
  );
};

export const CustomNodeComponent = memo(CustomNodeComponentInner);
